<template>
  <div class="upload-container" :class="{ 'drag-active': isDragging }" @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave" @drop.prevent="handleDrop">

    <input type="file" ref="fileInput" style="display: none" @change="handleFileChange"
      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple />

    <div class="d-flex align-items-center">
      <!-- Main Upload Button -->
      <b-button size="sm" style="min-width: 35px; position: relative;"
        :variant="pendingFilesCount > 0 ? 'warning' : (hasExistingFiles ? 'success' : 'outline-primary')"
        @click="$refs.fileInput.click()" v-b-tooltip.hover :title="tooltipText">

        <font-awesome-icon v-if="pendingFilesCount > 0" icon="clock" />
        <font-awesome-icon v-else-if="hasExistingFiles" icon="check" />
        <font-awesome-icon v-else icon="upload" />

        <b-badge v-if="pendingFilesCount > 0" variant="danger" class="pending-badge">
          {{ pendingFilesCount }}
        </b-badge>
      </b-button>

      <!-- NEW: Button to open Modal (only shows if files exist) -->
      <b-button v-if="hasExistingFiles" size="sm" variant="link" class="ml-1 px-1" @click="showFilesModal = true"
        v-b-tooltip.hover title="View Files">
        <font-awesome-icon icon="eye" />
      </b-button>
    </div>

    <b-modal v-model="showFilesModal" title="Attached Files" hide-footer centered size="md">
      <div v-if="existingFiles.length > 0" class="file-gallery">
        <div v-for="(file, idx) in existingFiles" :key="idx" class="file-item-container">

          <div class="file-preview-card">
            <!-- Thumbnail or Icon -->
            <div class="preview-box">
              <template v-if="isImage(file.url)">
                <img :src="file.url" class="img-thumbnail-custom" alt="preview" />
              </template>
              <template v-else>
                <div class="file-icon-placeholder">
                  <font-awesome-icon :icon="getFileIcon(file.url)" size="2x" />
                </div>
              </template>

              <!-- Delete Overlay (Visible on Hover) -->
              <div class="delete-overlay" @click="confirmDelete(idx)">
                <font-awesome-icon :icon="['fas', 'trash-alt']" />
              </div>
            </div>

            <!-- File Name & Link -->
            <div class="file-info">
              <a :href="file.url" target="_blank" class="file-name-text">
                <b-button variant="vb-primary">{{ getFileName(file.url) }}</b-button>
              </a>
            </div>
          </div>

        </div>
      </div>
      <div v-else class="text-center p-4 text-muted">
        <font-awesome-icon icon="folder-open" size="3x" class="mb-2 opacity-50" /><br>
        No files uploaded.
      </div>
    </b-modal>

  </div>
</template>

<script>
export default {
  props: ['row'],
  data() {
    return {
      isDragging: false,
      showFilesModal: false
    };
  },
  computed: {
    existingFiles() {
      if (!this.row.field_update_files || !Array.isArray(this.row.field_update_files)) return [];
      return this.row.field_update_files.filter(f => f.url && f.url !== "-");
    },
    hasExistingFiles() {
      return this.existingFiles.length > 0;
    },
    pendingFilesCount() {
      return (this.row._pendingFiles && this.row._pendingFiles.length) || 0;
    },
    tooltipText() {
      if (this.pendingFilesCount > 0) return `${this.pendingFilesCount} file(s) ready to save`;
      if (this.hasExistingFiles) return 'Add/Replace Files';
      return 'Upload Files';
    }
  },
  methods: {
    isImage(url) {
      if (!url) return false;
      const ext = url.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    },

    getFileIcon(url) {
      const ext = url.split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'file-pdf';
      if (['doc', 'docx'].includes(ext)) return 'file-word';
      return 'file';
    },

    getFileName(url) {
      if (!url) return "Unknown File";
      return decodeURIComponent(url.split('/').pop());
    },

    confirmDelete(index) {
      if (confirm("Are you sure you want to remove this file?")) {
        this.$emit('delete-file', index);
        if (this.existingFiles.length <= 1) this.showFilesModal = false;
      }
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) this.processFiles(files);
      event.target.value = '';
    },
    onDragOver() { this.isDragging = true; },
    onDragLeave() { this.isDragging = false; },
    handleDrop(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) this.processFiles(files);
    },
    processFiles(files) {
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
      const validFiles = files.filter(file => {
        const extension = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
        return allowedTypes.includes(`.${extension}`);
      });

      if (validFiles.length !== files.length) {
        this.$bvToast.toast("Some files were skipped due to invalid format", { variant: "warning" });
      }

      if (validFiles.length > 0) {
        this.$emit('files-selected', validFiles);
      }
    }
  }
};
</script>
<style scoped>
/* Modal Gallery Layout */
.file-gallery {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-preview-card {
  display: flex;
  align-items: center;
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: background 0.2s;
}

.file-preview-card:hover {
  background-color: #f8f9fa;
}

/* The Image/Icon Box */
.preview-box {
  position: relative;
  width: 100%;
  min-height: 100px;
  border-radius: 4px;
  background-color: #f1f1f1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.img-thumbnail-custom {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-icon-placeholder {
  color: #6c757d;
}

/* Delete Overlay Styling (The "Hover" part) */
.delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(220, 53, 69, 0.8);
  /* Red background with transparency */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  /* Hidden by default */
  transition: opacity 0.2s ease-in-out;
}

.preview-box:hover .delete-overlay {
  opacity: 1;
  /* Shows only on hover */
}

/* Text Styling */
.file-info {
  flex-grow: 1;
  overflow: hidden;
}

.file-name-text {
  font-size: 14px;
  color: #007bff;
  text-decoration: none;
  word-break: break-all;
}

.file-name-text:hover {
  text-decoration: underline;
}

/* Other existing styles... */
.upload-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pending-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  font-size: 10px;
}
</style>
